import { use, useEffect, useMemo, useReducer, useRef, type ReactNode } from 'react';

import { ClientContext } from '@/clients/ClientContext';
import {
    MapInteractionCommandsContext,
    MapInteractionStateContext,
    type MapInteractionCommands,
} from './MapInteractionContext';
import { mapInteractionReducer } from './mapInteractionReducer';
import { INITIAL_MAP_INTERACTION_STATE } from './mapInteractionState';

/**
 * Owns the map-interaction slice (selected feature + located highlight) for a map
 * route. Mounted on every route that renders an interactive MapView — the Find & Fix
 * layout route (state survives sub-route round-trips, like TableViewProvider) and the
 * SAVI network-overview route. The no-op hook fallbacks exist for unit tests only:
 * a production MapView must never run on them.
 *
 * Depends on ClientContext alone (read leniently so provider tests don't need a
 * ClientProvider): a client switch resets the whole slice — a selection or highlight
 * must never outlive the client whose features it points at.
 */
export function MapInteractionProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(mapInteractionReducer, INITIAL_MAP_INTERACTION_STATE);

    const clientId = use(ClientContext)?.selectedClient?.id ?? null;
    // A "switch" is a change AWAY from a resolved client id. The bootstrap resolve
    // (null → first id) is not a switch and must not reset state seeded before it.
    const prevClientIdRef = useRef<string | null>(null);
    useEffect(() => {
        const prev = prevClientIdRef.current;
        prevClientIdRef.current = clientId;
        if (prev === null || prev === clientId) return;
        dispatch({ type: 'CLIENT_CHANGED' });
    }, [clientId]);

    // dispatch is stable, so the commands object never changes identity — consumers
    // of the commands context alone never re-render on interaction-state changes.
    const commands = useMemo<MapInteractionCommands>(() => {
        const selectPoi: MapInteractionCommands['selectPoi'] = (poi, coords) => {
            dispatch({ type: 'SELECT_FEATURE', selection: { type: 'poi', poi, coords } });
        };
        return {
            selectPoi,
            showPoiDetails: selectPoi,
            selectLeak: (leak, coords) => {
                dispatch({ type: 'SELECT_FEATURE', selection: { type: 'leak', leak, coords } });
            },
            selectVan: (van, coords) => {
                dispatch({ type: 'SELECT_FEATURE', selection: { type: 'van', van, coords } });
            },
            selectArea: (name, lngLat) => {
                dispatch({ type: 'SELECT_FEATURE', selection: { type: 'aoi', name, lngLat } });
            },
            selectDma: (ident, name, lngLat) => {
                dispatch({ type: 'SELECT_FEATURE', selection: { type: 'dma', ident, name, lngLat } });
            },
            locatePoi: (id) => {
                dispatch({ type: 'LOCATE_FEATURE', feature: { type: 'poi', id }, origin: 'table' });
            },
            locateArea: (name) => {
                dispatch({ type: 'LOCATE_FEATURE', feature: { type: 'aoi', name }, origin: 'table' });
            },
            locateDma: (ident, origin = 'table') => {
                dispatch({ type: 'LOCATE_FEATURE', feature: { type: 'dma', ident }, origin });
            },
            clearSelection: () => {
                dispatch({ type: 'CLEAR_SELECTION' });
            },
            emptyMapClick: () => {
                dispatch({ type: 'EMPTY_MAP_CLICK' });
            },
            panelOpened: () => {
                dispatch({ type: 'PANEL_OPENED' });
            },
            tableClosed: (reason) => {
                dispatch({ type: 'TABLE_CLOSED', reason });
            },
            searchCleared: () => {
                dispatch({ type: 'SEARCH_CLEARED' });
            },
            resetToNetwork: () => {
                dispatch({ type: 'RESET_TO_NETWORK' });
            },
            activateTool: (tool) => {
                dispatch({ type: 'TOOL_ACTIVATED', tool });
            },
            deactivateTool: () => {
                dispatch({ type: 'TOOL_DEACTIVATED' });
            },
        };
    }, []);

    return (
        <MapInteractionCommandsContext value={commands}>
            <MapInteractionStateContext value={state}>{children}</MapInteractionStateContext>
        </MapInteractionCommandsContext>
    );
}
